import { useEffect, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { Menu, Dropdown, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import useI18n from '../../common/hooks/useI18n';
import LanguageSelector from './languageSelector';

type Props = {
  className?: string;
};

const FacebookLogo = styled.a`
  width: 22px;
  height: 22px;
  background-image: url(/images/facebook_logo.svg);
  background-size: 22px 22px;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  z-index: 1;
  margin-left: 6px;
  margin-right: 6px;
`;

const InstagramLogo = styled.a`
  width: 22px;
  height: 22px;
  background-image: url(/images/instagram_logo.svg);
  background-repeat: no-repeat;
  background-size: 22px 22px;
  background-position: center center;
  cursor: pointer;
  z-index: 1;
  margin-left: 6px;
  margin-right: 6px;
`;

const SocialMediaLinks = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DropdownContainer = styled.div`
  @media ${(props) => props.theme.breakpoints.lgu} {
    display: none;
  }
`;

const MenuButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-style: ${(props) => props.theme.font.style};
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;

  color: ${(props) => props.theme.colors.secondary};

  @media ${(props) => props.theme.breakpoints.mdd} {
    display: none;
  }
`;

const StyledLink = styled.a`
  font-style: ${(props) => props.theme.font.style};
`;

const StyledButton = styled(Button)`
  color: ${(props) => props.theme.colors.secondary};

  font-style: ${(props) => props.theme.font.style};
`;

const NucleoLabel = styled.p`
  padding: 4px 15px;
  font-style: ${(props) => props.theme.font.style};
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
`;

const Footer = ({ className }: Props) => {
  const i18n = useI18n();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const dropdownMenu = (
    <Menu>
      <Menu.Item key="0">
        <Link href="/legal/aviso" passHref rel="nofollow" legacyBehavior>
          <StyledLink>{i18n.t('footer.legalWarning')}</StyledLink>
        </Link>
      </Menu.Item>
      <Menu.Item key="1">
        <Link
          href="/legal/politica-privacidad"
          passHref
          rel="nofollow"
          legacyBehavior
        >
          <StyledLink>{i18n.t('footer.privacyPolicy')}</StyledLink>
        </Link>
      </Menu.Item>
      <Menu.Item key="2">
        <Link href="/legal/cookies" passHref rel="nofollow" legacyBehavior>
          <StyledLink>{i18n.t('footer.cookies')}</StyledLink>
        </Link>
      </Menu.Item>
      <Menu.Item key="2">
        <Link
          href="https://intranet-nucleo.vercel.app/"
          passHref
          rel="nofollow"
          legacyBehavior
        >
          <StyledLink>Intranet</StyledLink>
        </Link>
      </Menu.Item>
    </Menu>
  );

  const nucleoSlText = `© ${new Date().getFullYear()} Núcleo S.L.`;

  return (
    <footer className={className}>
      {isMounted && (
        <>
          <SocialMediaLinks>
            <FacebookLogo
              as="a"
              href="https://www.facebook.com/pages/category/Real-Estate-Agent/Inmobiliaria-N%C3%BAcleo-103831184774047/"
            />
            <InstagramLogo
              as="a"
              href="https://www.instagram.com/inmobiliarianucleo/"
            />
            <LanguageSelector />
          </SocialMediaLinks>
          <DropdownContainer>
            <Dropdown overlay={dropdownMenu} trigger={['click']}>
              <Button type="text" onClick={(e) => e.preventDefault()}>
                {nucleoSlText} <DownOutlined />
              </Button>
            </Dropdown>
          </DropdownContainer>
          <MenuButtons>
            <NucleoLabel>{nucleoSlText}</NucleoLabel>
            <Link
              href="https://intranet-nucleo.vercel.app/"
              passHref
              rel="nofollow"
            >
              <StyledButton type="text">Intranet</StyledButton>
            </Link>
            <Link href="/legal/aviso" passHref rel="nofollow">
              <StyledButton type="text">
                {i18n.t('footer.legalWarning')}
              </StyledButton>
            </Link>
            <Link href="/legal/politica-privacidad" passHref rel="nofollow">
              <StyledButton type="text">
                {i18n.t('footer.privacyPolicy')}
              </StyledButton>
            </Link>
            <Link href="/legal/cookies" passHref rel="nofollow">
              <StyledButton type="text">
                {i18n.t('footer.cookies')}
              </StyledButton>
            </Link>
          </MenuButtons>
        </>
      )}
    </footer>
  );
};

export default styled(Footer)`
  width: 100%;
  height: ${(props) => props.theme.footerHeight};

  background-color: white;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  @media ${(props) => props.theme.breakpoints.xxl} {
    padding-left: ${(props) => props.theme.grid.getGridColumns(1, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  }
`;
